/**
 * Cloudinary URL optimization helper.
 *
 * Injects `/f_auto,q_auto,...` transformations right after `/upload/`.
 * Cloudinary then serves WebP/AVIF (auto format) at optimal quality for
 * the requesting browser — typical savings of 40-70% vs raw uploads.
 *
 * Pass options to tune:
 *   - w  : max width (default 1200 for photos, 400 for logos)
 *   - q  : quality mode — "auto:eco" (aggressive), "auto:good" (balanced),
 *          "auto:best". Defaults to "auto:eco" for our freemium economics.
 *   - c  : crop mode ("limit" keeps aspect ratio within w, never upscales)
 */
export function cloudy(url, opts = {}) {
  if (!url || typeof url !== "string") return url;
  if (!url.includes("res.cloudinary.com") || !url.includes("/upload/")) return url;
  // Skip if a transformation is already present (idempotent).
  const marker = "/upload/";
  const [prefix, rest] = url.split(marker, 2);
  if (!rest) return url;
  const firstSeg = rest.split("/")[0];
  if (firstSeg && (firstSeg.startsWith("f_") || firstSeg.startsWith("q_") || firstSeg.startsWith("w_") || firstSeg.startsWith("c_"))) {
    return url; // Already transformed — don't stack.
  }
  const w = opts.w ?? 1200;
  const q = opts.q ?? "auto:eco";
  const c = opts.c ?? "limit";
  const transform = `f_auto,q_${q},c_${c},w_${w}`;
  return `${prefix}${marker}${transform}/${rest}`;
}

export function cloudyThumb(url) {
  return cloudy(url, { w: 400, q: "auto:eco" });
}

export function cloudyLogo(url) {
  return cloudy(url, { w: 300, q: "auto:good" });
}
